import { useEffect, useMemo, useState } from "react";
import type { KeyboardEvent } from "react";
import type { KeyValue, TileField } from "@fortex/api";
import type { InfoFeature } from "../../sources/types";
import { useCatalogue, useInfo, useLabels, useLayers, useMapCore } from "../hooks";
import { Icon } from "../icons";
import { isPhotoField, isValidJson, visibleFields } from "../fieldValues";
import { layerTitle } from "../layerTreeModel";
import { Button } from "../primitives/Button";
import { FieldEditor } from "./FieldEditor";

export type TileFeaturePanelProps = {
  feature: InfoFeature;
  saving: "idle" | "saving" | "saved" | "error";
  searchTerm?: string | null;
  error?: string | null;
  onSave: (data: KeyValue[]) => void;
  onDirtyChange?: ((dirty: boolean) => void) | null;
  onClose?: (() => void) | null;
};

function parentRow(key: string): TileField {
  return { parentField: null, key, humanName: "", visible: "All", editable: false, type: "Json", filterMapVisible: "None" };
}

export function fieldsOf(feature: InfoFeature, admin: boolean, pictures = true): TileField[] {
  const valueOf = (key: string) => feature.feature.data.find(a => a.key === key)?.value ?? null;
  const all = (feature.feature.fields ?? []).filter(a => pictures || !(a.type === "Json" && isPhotoField(a.key, valueOf(a.key))));
  const top = visibleFields(
    all.filter(a => a.parentField === null),
    admin
  );
  const parents = [...new Set(all.filter(a => a.parentField !== null).map(a => a.parentField as string))];
  const synthesized = parents.filter(key => !all.some(a => a.parentField === null && a.key === key) && feature.feature.data.some(a => a.key === key)).map(parentRow);
  const rows = [...top, ...synthesized];

  if (rows.length > 0) return rows;

  return feature.feature.data.filter(a => pictures || !isPhotoField(a.key, a.value)).map(a => ({ parentField: null, key: a.key, humanName: a.key, visible: "All", editable: false, type: "String", filterMapVisible: "None" }));
}

export function childFieldsOf(feature: InfoFeature, key: string): TileField[] {
  return (feature.feature.fields ?? []).filter(a => a.parentField === key);
}

export function featureSubtitle(feature: InfoFeature, layerName: string | null): string {
  return layerName ?? [feature.feature.layerGroup, feature.feature.layer ?? feature.hit.layer].filter(Boolean).join(" › ");
}

export function TileFeaturePanel({ feature, saving, error = null, searchTerm = null, onSave, onDirtyChange = null, onClose = null }: TileFeaturePanelProps) {
  const core = useMapCore();
  const labels = useLabels();
  const { api } = useInfo();
  const catalogue = useCatalogue();
  const { list } = useLayers();
  const subtitle = featureSubtitle(feature, layerTitle(catalogue, list, feature.hit.layer));
  const admin = core.options.info.admin ?? core.api.authenticated;
  const pictures = core.options.info.pictures;
  const fields = useMemo(() => fieldsOf(feature, admin, pictures), [feature, admin, pictures]);
  const original = useMemo(() => new Map<string, string | null>(feature.feature.data.map(a => [a.key, a.value])), [feature]);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [routing, setRouting] = useState(false);
  const editable = feature.editable && feature.feature.id !== null && fields.some(a => a.editable);
  const dirtyKeys = Object.keys(draft).filter(key => (original.get(key) ?? "") !== draft[key]);
  const dirty = dirtyKeys.length > 0;
  const invalid = fields.some(a => a.type === "Json" && a.key in draft && !isValidJson(draft[a.key]));
  const routable = core.options.controls.route && feature.feature.wkt !== null && feature.feature.wkt.length > 0;

  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  useEffect(() => {
    if (saving === "saved") setDraft({});
  }, [saving]);

  const reset = () => setDraft({});

  const save = () => {
    if (!dirty || invalid || saving === "saving") return;

    const data: KeyValue[] = fields.filter(a => a.editable).map(a => ({ key: a.key, value: a.key in draft ? draft[a.key] : (original.get(a.key) ?? null) }));

    onSave(data);
  };

  const route = async () => {
    if (routing) return;

    setRouting(true);

    try {
      await api.routeTo();
    } finally {
      setRouting(false);
    }
  };

  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "s") {
      event.preventDefault();
      save();
    }
  };

  return (
    <section className={["fxmap-tile-feature", dirty ? "fxmap-tile-feature-dirty" : ""].filter(Boolean).join(" ")} data-fxmap="tile-feature" aria-label={labels.featureTitle} onKeyDown={onKeyDown}>
      <span className="fxmap-tile-feature-stripe" aria-hidden="true" />
      <header className="fxmap-tile-feature-head">
        <div className="fxmap-tile-feature-titles">
          <h2 className="fxmap-tile-feature-title fxmap-display fxmap-truncate">{feature.hit.name.length > 0 ? feature.hit.name : labels.featureTitle}</h2>
          <span className="fxmap-tile-feature-subtitle fxmap-muted fxmap-truncate" data-fxmap="tile-feature-layer">{subtitle}</span>
        </div>
        {routable ? <Button kind="icon" size="sm" icon="locate" label={labels.routeTo} action="route" busy={routing} onClick={() => void route()} /> : null}
        {onClose !== null ? <Button kind="icon" size="sm" icon="close" label={labels.close} onClick={onClose} action="close" /> : null}
      </header>
      {fields.length === 0 ? (
        <p className="fxmap-tile-feature-empty fxmap-muted">{labels.featureNoFields}</p>
      ) : (
        <div className="fxmap-tile-feature-fields" role="list">
          {fields.map(field => (
            <FieldEditor
              key={field.key}
              field={field}
              value={field.key in draft ? draft[field.key] : (original.get(field.key) ?? null)}
              editable={editable}
              dirty={dirtyKeys.includes(field.key)}
              childFields={childFieldsOf(feature, field.key)}
              admin={admin}
              searchTerm={searchTerm}
              onChange={value => setDraft(previous => ({ ...previous, [field.key]: value }))}
            />
          ))}
        </div>
      )}
      {editable ? (
        <footer className={["fxmap-tile-feature-footer", `fxmap-tile-feature-footer-${saving}`].join(" ")}>
          <span className="fxmap-tile-feature-status" role="status" aria-live="polite">
            {saving === "saving" ? (
              labels.saving
            ) : saving === "saved" && !dirty ? (
              <>
                <Icon name="success" />
                {labels.featureSaved}
              </>
            ) : saving === "error" ? (
              <>
                <Icon name="error" />
                {error !== null ? `${labels.featureSaveFailed}: ${error}` : labels.featureSaveFailed}
              </>
            ) : dirty ? (
              labels.unsavedChanges
            ) : (
              ""
            )}
          </span>
          <span className="fxmap-tile-feature-actions">
            <Button kind="ghost" size="sm" label={labels.cancel} disabled={!dirty || saving === "saving"} onClick={reset} action="cancel" />
            <Button kind="primary" size="sm" label={labels.save} disabled={!dirty || invalid} busy={saving === "saving"} onClick={save} data-fxmap="tile-feature-save" title={`${labels.save} (Ctrl+S)`} />
          </span>
        </footer>
      ) : null}
    </section>
  );
}
